import { useEffect, useState, useRef } from "react";

import { isEmpty, isEqual, values } from "lodash";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useSearchParams } from "react-router-dom";
import { getBriefData, setBriefData as setBriefDataStore } from "store/slice/Project/projectData/ProjectSlice";
import { getCharts, fetchCharts } from "store/slice/charts";
import useGraphsFetch from "utils/hooks/useGraphsFetch";
import { BriefChildren, TBriefChart } from "services/projects/project.types";
import { TBriefData } from "store/slice/Project/projectData/ProjectSlice.types";
import { getSearchAiLeadersFilters, getProjectSearchAiBrief } from "pages/ProjectDashboard";
import { facetToNarrative } from "utils";
import { getSearchAiById } from "store/slice/Project/searchAis/searchAisSlice";
import { shallowEqual } from "react-redux";
import { fetchProjectBrief } from "store/slice/Project/briefs/briefsSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { toggleLoader } from "store/slice/UI";

export const useBriefProfile = ({
    shouldFetchBrief,
}: {
    shouldFetchBrief: boolean;
}): {
    briefData: TBriefData;
    briefDataStore: TBriefData;
    chartStatus: RequestInfo;
    wasBriefModified: boolean;
    showBriefError: boolean;
    showConfirmBriefChanges: boolean;
    briefChangesConfirmed: boolean;
    setShowBriefError: React.Dispatch<React.SetStateAction<boolean>>;
    handleRevertChanges: () => void;
    handleSetBriefData: (data: Partial<TBriefData>) => void;
    handleUpdateBriefData: (data: Partial<TBriefChart>, chartIndex: "chart1" | "chart2") => void;
    handleConfirmChanges: (value: boolean) => void;
    setBriefChangesConfirmed: React.Dispatch<React.SetStateAction<boolean>>;
    handleShowConfirmationModal: () => void;
} => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const graphData = useGraphsFetch(["LD4", "LD5"], false);

    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const searchAiId = searchParams.get("searchAiId");

    const briefDataStore = useAppSelector(getBriefData);
    const { data: chartData, status: chartStatus } = useAppSelector(getCharts);
    const { data: searchAiData, status: searchAiStatus } = useAppSelector(
        getSearchAiById(Number(searchAiId)),
        shallowEqual
    );

    const [briefData, setBriefData] = useState<TBriefData>({} as TBriefData);
    const [showBriefError, setShowBriefError] = useState(false);
    const [showConfirmBriefChanges, setShowConfirmBriefChanges] = useState(false);
    const [briefChangesConfirmed, setBriefChangesConfirmed] = useState(false);
    const [wasBriefModified, setWasBriefModified] = useState(false);

    const avoidEqualityCheck = useRef(true);

    const handleRevertChanges = () => {
        avoidEqualityCheck.current = false;
        if (showBriefError) {
            setShowBriefError(false);
        }
        setBriefData(briefDataStore);
        setWasBriefModified(false);
    };

    const handleConfirmChanges = (value: boolean) => {
        if (value) {
            setBriefChangesConfirmed(value);
            setShowBriefError(false);
        }
        setShowConfirmBriefChanges(!showConfirmBriefChanges);
    };

    const handleShowConfirmationModal = () => {
        setShowConfirmBriefChanges(!showConfirmBriefChanges);
    };

    const handleSetBriefData = (data: Partial<TBriefData>) => {
        avoidEqualityCheck.current = false;
        setBriefData({ ...briefData, ...data });
    };

    const handleUpdateBriefData = (data: Partial<TBriefChart>, chartIndex: "chart1" | "chart2") => {
        handleSetBriefData({
            ...briefData,
            [chartIndex]: {
                ...briefData[`${chartIndex}`],
                ...data,
            },
        });
    };

    // Check if Brief was modified
    useEffect(() => {
        if (searchAiStatus === "done" && shouldFetchBrief && !avoidEqualityCheck.current) {
            const changed = !isEqual(briefData, briefDataStore);

            setWasBriefModified(changed);

            if (changed) {
                setBriefChangesConfirmed(!changed);
            }
        }
    }, [briefData, briefDataStore, shouldFetchBrief, searchAiStatus]);

    // Fetch chart
    useEffect(() => {
        const fetchChartsData = async () => {
            if (
                projectId &&
                insightId &&
                !isEmpty(searchAiData) &&
                !isEmpty(briefData) &&
                isEmpty(chartData) &&
                shouldFetchBrief &&
                graphData.target_company_board.length &&
                chartStatus === "pristine"
            ) {
                try {
                    await dispatch(fetchCharts({ graphData, projectId, insightId }));
                } catch (error) {
                    console.error(error);
                } finally {
                    dispatch(toggleLoader(false));
                }
            }
        };
        fetchChartsData();
    }, [chartData, chartStatus, dispatch, graphData, searchAiData, briefData, insightId, projectId, shouldFetchBrief]);

    // Fetch Brief
    useEffect(() => {
        const fetchBriefData = async () => {
            if (!isEmpty(searchAiData) && isEmpty(briefData) && shouldFetchBrief && projectId) {
                dispatch(toggleLoader(true));
                const briefPath = getProjectSearchAiBrief(searchAiData)?.ref?.path ?? "";

                if (!briefPath) return;

                const projectBrief: BriefChildren = unwrapResult(
                    await dispatch(
                        fetchProjectBrief({
                            projectId: Number(projectId),
                            path: briefPath,
                        })
                    )
                );

                const originalBriefData = {
                    position: projectBrief.data.crm_vacancy,
                    roleFunction: projectBrief.data.role_function,
                    roleFunctions: [projectBrief.data.role_function],
                    roleLevel: projectBrief.data.role_level,
                    roleLevels: [projectBrief.data.role_level],
                    chart1: projectBrief.data.functional_balance,
                    chart2: projectBrief.data.individual_positioning,
                    facets: {
                        situational: projectBrief.data.facet_situational.narratives.map(facetToNarrative),
                        domain: projectBrief.data.facet_domain.narratives.map(facetToNarrative),
                        functional: projectBrief.data.facet_functional.narratives.map(facetToNarrative),
                    },
                };

                dispatch(setBriefDataStore(originalBriefData));

                const searchAIFilters = getSearchAiLeadersFilters(searchAiData)?.data;

                if (!searchAIFilters) return;

                const isNewSearchAIPeople =
                    values(searchAIFilters.functional_balance).every((prop) => prop === null) &&
                    values(searchAIFilters.individual_positioning).every((prop) => prop === null);

                if (isNewSearchAIPeople) {
                    setBriefData(originalBriefData);
                }

                if (!isNewSearchAIPeople) {
                    const filtersSavedBrief = {
                        position: projectBrief.data.crm_vacancy,
                        roleFunction: projectBrief.data.role_function,
                        roleFunctions: [projectBrief.data.role_function],
                        roleLevel: projectBrief.data.role_level,
                        roleLevels: [projectBrief.data.role_level],
                        chart1: searchAIFilters.functional_balance,
                        chart2: searchAIFilters.individual_positioning,
                        facets: {
                            situational: searchAIFilters.facet_situational.narratives.map(facetToNarrative),
                            domain: searchAIFilters.facet_domain.narratives.map(facetToNarrative),
                            functional: searchAIFilters.facet_functional.narratives.map(facetToNarrative),
                        },
                    };

                    if (!isEqual(filtersSavedBrief, originalBriefData)) {
                        avoidEqualityCheck.current = false;
                    }
                    setBriefData(filtersSavedBrief);
                }

                if (!isEmpty(chartData)) {
                    dispatch(toggleLoader(false));
                }
            }
        };

        fetchBriefData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchAiData, briefData, shouldFetchBrief]);

    return {
        briefData,
        briefDataStore,
        chartStatus,
        wasBriefModified,
        showBriefError,
        showConfirmBriefChanges,
        briefChangesConfirmed,
        setShowBriefError,
        handleRevertChanges,
        handleSetBriefData,
        handleUpdateBriefData,
        handleConfirmChanges,
        setBriefChangesConfirmed,
        handleShowConfirmationModal,
    };
};

export default useBriefProfile;
